import React, { useEffect } from 'react';
import styles from '../../styles/dialog.module.scss';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Box } from '@mui/material';

const Dialog = ({
  open = false,
  onClose,
  title,
  children,
  maxWidth = 'sm', // xs, sm, md, lg, xl, false
  fullWidth = true,
  fullScreen = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  hideCloseButton = false,
  className = '',
  PaperProps = {},
  ...props
}) => {
  // Handle ESC key press
  useEffect(() => {
    if (!open || disableEscapeKeyDown) return;

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [open, disableEscapeKeyDown, onClose]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [open]);

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !disableBackdropClick) {
      onClose?.();
    }
  };

  // Don't render if not open
  if (!open) return null;

  const getDialogClasses = () => {
    let classes = styles.dialog;
    if (fullScreen) classes += ` ${styles.fullScreen}`;
    if (className) classes += ` ${className}`;
    return classes;
  };

  const getPaperClasses = () => {
    let classes = styles.dialogPaper;
    if (fullWidth) classes += ` ${styles.fullWidth}`;
    if (fullScreen) classes += ` ${styles.fullScreenPaper}`;
    if (maxWidth && maxWidth !== false) classes += ` ${styles[`maxWidth${maxWidth.charAt(0).toUpperCase() + maxWidth.slice(1)}`]}`;
    if (PaperProps.className) classes += ` ${PaperProps.className}`;
    return classes;
  };

  return (
    <div className={getDialogClasses()} {...props}>
      <div className={styles.backdrop} onClick={handleBackdropClick} />
      <div className={styles.container}>
        <div className={getPaperClasses()} {...PaperProps}>
          {/* Header */}
          {(title || !hideCloseButton) && (
            <div className={styles.dialogTitle}>
              {title && <h2 className={styles.titleText}>{title}</h2>}
              {!hideCloseButton && (
                <Box className={styles.closeButton} onClick={onClose} aria-label="Close dialog">
                  <XMarkIcon className={styles.closeIcon} />
                </Box>
              )}
            </div>
          )}

          {/* Content */}
          <div className={styles.dialogContent}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// Dialog Title Component
const DialogTitle = ({ children, className = '', ...props }) => (
  <div className={`${styles.dialogTitleCustom} ${className}`} {...props}>
    {children}
  </div>
);

// Dialog Content Component
const DialogContent = ({ children, className = '', dividers = false, ...props }) => (
  <div 
    className={`${styles.dialogContentCustom} ${dividers ? styles.dividers : ''} ${className}`} 
    {...props}
  >
    {children}
  </div>
);

// Dialog Actions Component
const DialogActions = ({ children, className = '', ...props }) => (
  <div className={`${styles.dialogActions} ${className}`} {...props}>
    {children}
  </div>
);

// Dialog Content Text Component
const DialogContentText = ({ children, className = '', ...props }) => (
  <p className={`${styles.dialogContentText} ${className}`} {...props}>
    {children}
  </p>
);

// Export all components
Dialog.Title = DialogTitle;
Dialog.Content = DialogContent;
Dialog.Actions = DialogActions;
Dialog.ContentText = DialogContentText;

export default Dialog;
export { DialogTitle, DialogContent, DialogActions, DialogContentText };
